"use client";

import {
  ShieldCheckIcon,
  ChartBarIcon,
  CommandLineIcon,
  ChartPieIcon,
  DevicePhoneMobileIcon,
  CircleStackIcon,
} from "@heroicons/react/24/outline";
import Link from "next/link";
import { useState } from "react";

const BentoFeatures = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const card =
    "group relative rounded-2xl border bg-white/80 p-6 shadow-sm transition-all " +
    "hover:-translate-y-0.5 hover:shadow-md transition-transform " +
    "border-gray-200 " +
    "dark:border-white/10 dark:bg-white/5 dark:backdrop-blur";

  const title =
    "mt-2 text-lg font-semibold text-[#002243] dark:text-white tracking-tight";
  const body = "mt-2 text-sm leading-6 text-[#002243]/80 dark:text-gray-300";

  const iconWrap =
    "inline-flex h-9 w-9 items-center justify-center rounded-xl " +
    "bg-[#348ddb]/10 text-[#348ddb] ring-1 ring-[#348ddb]/20 " +
    "dark:bg-[#348ddb]/15 dark:text-[#8cc8ff] dark:ring-[#8cc8ff]/20";

  return (
    <div className=" bg-white from-bgHome via-[#172035] to-bgHome dark:border-gray-700 dark:bg-gradient-to-r">
      <div className="relative isolate overflow-hidden">
        <div className="py-24 sm:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center">
              <h2 className="text-base font-semibold leading-7 text-[#348ddb]">
                Features
              </h2>
              <p className="mt-2 text-pretty text-3xl font-medium tracking-tight text-[#002243] dark:text-white lg:text-balance">
                Ethically collected data that is clean, standardized and
                securely stored available for analytics
              </p>
              <p className="mt-6 text-lg/8 text-[#002243] dark:text-gray-400">
                Accelerate discovery. Streamline months of data preparation into
                weeks of productive research—spend your time on insights, not
                infrastructure.
              </p>
            </div>

            {/* Bento grid */}
            <div className="mx-auto mt-16 grid max-w-5xl grid-cols-6 gap-4 sm:my-20 sm:gap-6">
              {/* Secure/Privacy-Preserving and IRB-Approved Infrastructure */}
              <Link
                href={"/post-level-data"}
                className={`col-span-6 hover:cursor-pointer sm:col-span-4 ${card}`}
              >
                <span className={iconWrap}>
                  <ShieldCheckIcon className="h-5 w-5" />
                </span>
                <h3 className={title}>
                  Secure/Privacy-Preserving & IRB-Approved Infrastructure
                </h3>
                <p className={body}>
                  Our Databricks-powered infrastructure is IRB-approved and
                  designed for academic research, offering secure, modular
                  computing with ethical safeguards, role-based access, audit
                  trails, and collaborative Python/SQL workflows.
                </p>

                {/* Accent line */}
                <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
              </Link>

              {/* Multi-Dimensional Analytics */}
              <Link
                href={"/post-level-data"}
                className={`col-span-6 hover:cursor-pointer sm:col-span-2 ${card}`}
              >
                <span className={iconWrap}>
                  <ChartBarIcon className="h-5 w-5" />
                </span>
                <h3 className={title}>Multi-Dimensional Analytics</h3>
                <p className={body}>
                  We combine quantitative engagement metrics with qualitative
                  content analysis, enabling rich insights from comprehensive
                  comment threads and user interactions.
                </p>
                {/* Accent line */}
                <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
              </Link>

              {/* Strategic Coverage through Ethical Sampling */}
              <Link
                href={"/post-level-data"}
                className={`col-span-6 hover:cursor-pointer sm:col-span-2 ${card}`}
              >
                <span className={iconWrap}>
                  <ChartPieIcon className="h-5 w-5" />
                </span>
                <h3 className={title}>
                  Strategic Coverage through Ethical Sampling
                </h3>
                <p className={body}>
                  Our sampling strategy ensures broad platform representation,
                  covering the top 2.3% of YouTube channels (~90% of
                  subscriptions) and ≥50% of Telegram subscriptions from
                  high-subscriber channels.
                </p>
                {/* Accent line */}
                <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
              </Link>

              {/* YouTube Dataset */}
              <Link
                href={"/post-level-data"}
                className={`col-span-6 hover:cursor-pointer sm:col-span-4 ${card}`}
              >
                <span className={iconWrap}>
                  <CommandLineIcon className="h-5 w-5" />
                </span>
                <h3 className={title}>YouTube Dataset</h3>
                <p className={body}>
                  The YouTube dataset includes full channel and video metadata,
                  monthly follower tracking, post-publication engagement stats,
                  and complete comment thread interactions.
                </p>
                {/* Accent line */}
                <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
              </Link>

              {/* Telegram Dataset */}
              <Link
                href={"/post-level-data"}
                className={`col-span-6 hover:cursor-pointer sm:col-span-3 ${card}`}
              >
                <span className={iconWrap}>
                  <CircleStackIcon className="h-5 w-5" />
                </span>
                <h3 className={title}>Telegram Dataset</h3>
                <p className={body}>
                  Telegram data captures channel and group metadata, subscriber
                  growth, post content and engagement metrics, along with full
                  comment threads and user interactions.
                </p>
                {/* Accent line */}
                <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
              </Link>

              {/* Princeton SMART */}
              <Link
                href={"/princeton-smart"}
                className={`col-span-6 hover:cursor-pointer sm:col-span-3 ${card}`}
              >
                <span className={iconWrap}>
                  <DevicePhoneMobileIcon className="h-5 w-5" />
                </span>
                <h3 className={title}>Princeton SMART</h3>
                <p className={body}>
                  Princeton SMART collects screenshot-based metrics from Android
                  users to enhance behavioral insights.
                </p>
                {/* Accent line */}
                <span className="pointer-events-none absolute inset-x-0 -bottom-px h-px bg-gradient-to-r from-transparent via-[#348ddb]/40 to-transparent" />
              </Link>
            </div>

            <div className="mt-10 flex items-center justify-center gap-x-6">
              <a
                href="/auth/login?screen_hint=signup&returnTo=/auth/verify-email"
                className="text-md cursor-pointer rounded-lg border border-[#002243] bg-[#F4F6FB] px-4 py-2 font-semibold leading-6 text-[#002243] shadow-sm hover:bg-[#F4F6FB]/80 hover:bg-[#e9ebf0] focus:outline-none focus:ring-2 focus:ring-[#348ddb] dark:border-white/20 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
              >
                Start Your Research
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BentoFeatures;
